import { Injectable, NgZone } from '@angular/core';
import {
  Canvas,
  Circle,
  FabricImage,
  FabricObject,
  FabricText,
  Gradient,
  Group,
  IText,
  Pattern,
  Path,
  Polygon,
  Rect,
  Shadow,
  Triangle,
} from 'fabric';
import { BehaviorSubject, Subject } from 'rxjs';
import QRCode from 'qrcode';
import { DEFAULT_BRUSH_STYLE, EditorLayer, EditorLayerType, EditorScene } from '../models/editor-layer.model';

/** Working canvas resolution per aspect ratio — chosen for crisp text/handles at typical zoom levels. Export applies an extra multiplier on top of this (see exportToBlob). */
export const ARTBOARD_SIZE: Record<'3:4' | '1:1' | '9:16', { width: number; height: number }> = {
  '3:4': { width: 900, height: 1200 },
  '1:1': { width: 1000, height: 1000 },
  '9:16': { width: 810, height: 1440 },
};

/**
 * Custom field fabric.js lets us stash on any object — used to round-trip our
 * own layer schema. `props` is the FULL canonical prop bag for the layer and
 * is the single source of truth for round-tripping (getScene()/toLayer()
 * reads from here rather than reverse-engineering live Fabric object state,
 * which matters for composite renders like curved text — a Fabric Group of
 * individual characters can't be reverse-parsed back into "text/font/curve").
 */
interface FabricLayerData {
  layerId: string;
  layerType: EditorLayerType;
  props: Record<string, any>;
}

type TaggedFabricObject = FabricObject & { data?: FabricLayerData };

/** Points for a regular n-gon, point-up (first vertex at the top). Used for diamond(4)/pentagon(5)/hexagon(6). */
function regularPolygonPoints(sides: number, r: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = ((2 * Math.PI) / sides) * i - Math.PI / 2;
    pts.push({ x: r * Math.cos(angle), y: r * Math.sin(angle) });
  }
  return pts;
}

/** Points for an n-pointed star, alternating outer/inner radius. */
function starPoints(
  spikes: number,
  outerR: number,
  innerR: number,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  const step = Math.PI / spikes;
  for (let i = 0; i < 2 * spikes; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = i * step - Math.PI / 2;
    pts.push({ x: r * Math.cos(angle), y: r * Math.sin(angle) });
  }
  return pts;
}

/** Points for a horizontal double-headed arrow, in a local 0..100 x 0..40 box. */
function arrowPoints(): { x: number; y: number }[] {
  return [
    { x: 0, y: 20 },
    { x: 25, y: 0 },
    { x: 25, y: 12 },
    { x: 75, y: 12 },
    { x: 75, y: 0 },
    { x: 100, y: 20 },
    { x: 75, y: 40 },
    { x: 75, y: 28 },
    { x: 25, y: 28 },
    { x: 25, y: 40 },
  ];
}

const HEART_PATH =
  'M 12 21 C 12 21 2 14 2 8 C 2 4.5 4.5 2 8 2 C 10 2 11.5 3 12 4.5 C 12.5 3 14 2 16 2 C 19.5 2 22 4.5 22 8 C 22 14 12 21 12 21 Z';

const SPEECH_BUBBLE_PATH =
  'M10,0 L90,0 Q100,0 100,10 L100,60 Q100,70 90,70 L35,70 L15,90 L20,70 L10,70 Q0,70 0,60 L0,10 Q0,0 10,0 Z';

/**
 * Wraps the Fabric.js canvas lifecycle for the image editor. See
 * IMAGE_EDITOR_REQUIREMENTS.md sections 4 and 8 — this implements the export
 * gotchas documented there (font-load race, CORS-tainted canvas via blob URLs).
 *
 * Zoom/pan are deliberately NOT handled in here — the host component applies
 * a CSS transform: scale() to the artboard wrapper instead (Fabric correctly
 * remaps pointer coordinates through a CSS-scaled canvas element, so this is
 * simpler and gives Photoshop/Figma-style "canvas zoom" behavior for free
 * without touching Fabric's own viewport transform).
 */
@Injectable()
export class ImageEditorCanvasService {
  private canvas?: Canvas;
  private aspectRatio: '3:4' | '1:1' | '9:16' = '3:4';

  /**
   * The artboard's LIVE dimensions — seeded from ARTBOARD_SIZE[aspectRatio]
   * at initCanvas(), but resizeArtboard() can override it afterwards (the
   * "resize the artboard itself" feature). Every internal use of "the
   * artboard size" (centering new layers, covering the background image)
   * reads this, not the static preset table, so it stays correct post-resize.
   */
  private currentSize: { width: number; height: number } = ARTBOARD_SIZE['3:4'];

  /** Clipboard for Ctrl/Cmd+C / Ctrl/Cmd+V — a cloned Fabric object, re-cloned (with a fresh layerId) on each paste. */
  private clipboard: FabricObject | null = null;
  private pasteOffset = 0;

  /** Snap-to-grid — the grid itself is drawn by the host component as a plain CSS overlay above the canvas element (never a Fabric object), so it can never end up in the exported PNG. This flag/size just controls whether object:moving hard-snaps to that same spacing. */
  private snapEnabled = false;
  private gridSize = 50;

  /**
   * Set once any image on the canvas had to fall back to a plain, non-CORS
   * load (see loadImageSafely/setImageSrcSafely tier 3 below). A canvas with
   * even one such image is "tainted" — the browser refuses to read its pixel
   * data at all — so exportToBlob() checks this to turn an opaque
   * SecurityError into an actionable message instead of failing silently.
   */
  private taintedExport = false;

  /** Emits the currently selected layer (or null) any time selection changes — null both when nothing AND when multiple objects are selected (see selectionCount$ for the latter). */
  readonly selection$ = new BehaviorSubject<EditorLayer | null>(null);
  /** Emits how many objects are currently selected — the topbar's multi-select alignment menu shows only when this is 2+. */
  readonly selectionCount$ = new BehaviorSubject<number>(0);
  /** Emits whenever the scene changes (add/remove/modify) — host can use this to flip a "dirty" flag. */
  readonly changed$ = new Subject<void>();

  constructor(private zone: NgZone) {}

  get artboardSize() {
    return this.currentSize;
  }

  /** Toggles hard snap-to-grid while dragging objects, and/or updates the grid spacing (canvas px, at the artboard's native resolution). */
  setSnap(enabled: boolean, gridSize?: number): void {
    this.snapEnabled = enabled;
    if (gridSize) this.gridSize = gridSize;
  }

  /**
   * Resizes the artboard itself (not any object on it) — the canvas element's
   * own pixel dimensions. Anchored top-left: existing objects keep their
   * current x/y, so growing the artboard adds space to the right/bottom and
   * shrinking it may crop content off the right/bottom edge, exactly like
   * Photoshop's default "Canvas Size" anchor. Existing background-image
   * layers are deliberately NOT auto-rescaled to re-cover the new size —
   * the user can drag/replace as needed, consistent with that same precedent.
   */
  resizeArtboard(width: number, height: number): void {
    if (!this.canvas) return;
    this.currentSize = { width, height };
    this.canvas.setDimensions({ width, height });
    this.canvas.requestRenderAll();
    this.changed$.next();
  }

  initCanvas(
    canvasElement: HTMLCanvasElement,
    aspectRatio: '3:4' | '1:1' | '9:16',
    scene?: EditorScene,
  ): void {
    this.aspectRatio = aspectRatio;
    const { width, height } = ARTBOARD_SIZE[aspectRatio];
    this.currentSize = { width, height };

    // Fabric's default selection chrome is a near-invisible pale blue
    // against a busy artboard/checkerboard backdrop. Override the shared
    // per-object defaults once so every object (and every layer type) gets
    // a clearly visible selection outline + solid corner handles.
    Object.assign(FabricObject.ownDefaults, {
      borderColor: '#2563eb',
      borderScaleFactor: 2,
      cornerColor: '#2563eb',
      cornerStrokeColor: '#ffffff',
      cornerStyle: 'circle' as const,
      cornerSize: 10,
      transparentCorners: false,
    });

    this.zone.runOutsideAngular(() => {
      this.canvas = new Canvas(canvasElement, {
        width,
        height,
        backgroundColor: '#ffffff',
        preserveObjectStacking: true,
        selection: true,
        // Fabric only listens for Shift by default (selectionKey); adding
        // Ctrl explicitly so either modifier adds/removes an object from
        // the selection on click, and drag-selecting empty artboard space
        // still works unmodified either way.
        selectionKey: ['shiftKey', 'ctrlKey'],
      } as any);

      const emitSelection = () => {
        this.zone.run(() => {
          const count = this.canvas?.getActiveObjects().length ?? 0;
          this.selectionCount$.next(count);
          const active = this.canvas?.getActiveObject() as TaggedFabricObject | undefined;
          // A multi-selection (Fabric's ActiveSelection) has no .data of its
          // own, so toLayer() would fabricate a bogus single-layer entry for
          // it — emit null instead and let the topbar's align menu (driven
          // by selectionCount$) handle the multi-select case.
          this.selection$.next(active && count <= 1 ? this.toLayer(active) : null);
        });
      };
      const emitChanged = () => this.zone.run(() => this.changed$.next());

      // Native inline text editing (double-click an IText on the canvas)
      // mutates the Fabric object's own `.text` directly — bypassing our
      // data.props bag entirely. Without this, data.props.text stays frozen
      // at whatever it was before the edit, and the NEXT sidebar attribute
      // change (align, color, font, ...) rebuilds the object from that
      // stale props bag, silently reverting whatever the user just typed.
      // Syncing it here on every keystroke keeps data.props authoritative.
      const emitTextChanged = (e: { target?: FabricObject }) => {
        this.zone.run(() => {
          const target = e.target as TaggedFabricObject | undefined;
          if (target?.data && 'text' in target) {
            target.data.props = { ...target.data.props, text: (target as unknown as { text: string }).text };
          }
          this.changed$.next();
        });
      };

      this.canvas.on('selection:created', emitSelection);
      this.canvas.on('selection:updated', emitSelection);
      this.canvas.on('selection:cleared', emitSelection);
      this.canvas.on('object:modified', emitChanged);
      this.canvas.on('object:modified', emitSelection);
      this.canvas.on('text:changed', emitTextChanged);

      // Snap-to-grid: hard-snaps the object's center to the nearest grid
      // intersection while dragging, whenever enabled (see setSnap()).
      this.canvas.on('object:moving', (e) => {
        if (!this.snapEnabled) return;
        const target = e.target;
        if (!target) return;
        const gs = this.gridSize;
        target.set({
          left: Math.round((target.left ?? 0) / gs) * gs,
          top: Math.round((target.top ?? 0) / gs) * gs,
        });
      });
    });

    if (scene) {
      scene.layers
        .slice()
        .sort((a, b) => a.zIndex - b.zIndex)
        .forEach((layer) => this.addLayer(layer));
    }
  }

  /**
   * Loads a remote image with three fallback tiers, from safest to
   * last-resort — this matters because a naive fetch-then-crossOrigin-retry
   * (the old approach) actively made things WORSE for CORS-restricted hosts:
   * if fetch() is blocked by CORS, an `<img crossOrigin="anonymous">` load of
   * the same URL almost always fails too (both are gated by the same
   * Access-Control-Allow-Origin check), so the image never appeared at all —
   * silently, since nothing downstream caught the rejection either.
   *
   * 1. fetch() -> blob -> same-origin blob: URL. The ideal path: never
   *    taints the canvas, works whenever the host sends CORS headers.
   * 2. `crossOrigin: 'anonymous'` load of the original URL. Covers hosts
   *    that allow anonymous image reads even though their fetch/XHR CORS
   *    policy is stricter (a real, if uncommon, split some CDNs have).
   * 3. Plain load of the original URL, no crossOrigin. Always works
   *    visually (browsers don't block a normal <img> from *displaying* a
   *    cross-origin image, only from being read as pixels) but taints the
   *    canvas for export — flagged via taintedExport so exportToBlob() can
   *    surface a clear error instead of a silent failure.
   */
  private async loadImageSafely(sourceUrl: string): Promise<FabricImage> {
    if (sourceUrl.startsWith('data:') || sourceUrl.startsWith('blob:')) {
      return FabricImage.fromURL(sourceUrl);
    }
    try {
      const res = await fetch(sourceUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return await FabricImage.fromURL(URL.createObjectURL(blob));
    } catch {
      // tier 1 failed (commonly a cross-origin host with no CORS headers on fetch) — try tier 2.
    }
    try {
      return await FabricImage.fromURL(sourceUrl, { crossOrigin: 'anonymous' });
    } catch {
      // tier 2 failed too — fall through to the last-resort tier 3.
    }
    this.taintedExport = true;
    return FabricImage.fromURL(sourceUrl);
  }

  /** Same three-tier fallback as loadImageSafely(), but swaps the source of an existing Fabric image object in place (used by replaceImageLayerSource). */
  private async setImageSrcSafely(obj: FabricImage, sourceUrl: string): Promise<void> {
    if (sourceUrl.startsWith('data:') || sourceUrl.startsWith('blob:')) {
      await obj.setSrc(sourceUrl);
      return;
    }
    try {
      const res = await fetch(sourceUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      await obj.setSrc(URL.createObjectURL(blob));
      return;
    } catch {
      // tier 1 failed — try tier 2.
    }
    try {
      await obj.setSrc(sourceUrl, { crossOrigin: 'anonymous' });
      return;
    } catch {
      // tier 2 failed too — fall through to the last-resort tier 3.
    }
    this.taintedExport = true;
    await obj.setSrc(sourceUrl);
  }

  /**
   * Builds a multi-stop linear gradient from a `{offset, color}[]` list
   * (offsets are 0-100, i.e. percent). Uses `gradientUnits: 'percentage'`
   * with 0..1 coords — the size-independent way to do an object-relative
   * gradient in Fabric — deliberately NOT `gradientUnits: 'pixels'` with
   * hardcoded coords, which is what silently rendered as a solid block of
   * the first color before: pixel-space coords are measured from the
   * object's un-rotated top-left corner, and every shape here is built with
   * originX/originY 'center', so a 0..160 coordinate range straddled mostly
   * *outside* the visible bounding box and got clamped to the nearest stop.
   */
  private buildGradient(stops?: { offset: number; color: string }[]): Gradient<'linear'> {
    const list =
      stops && stops.length >= 2
        ? stops
        : [
            { offset: 0, color: '#2781ff' },
            { offset: 100, color: '#ff2ee1' },
          ];
    return new Gradient({
      type: 'linear',
      gradientUnits: 'percentage',
      coords: { x1: 0, y1: 0, x2: 1, y2: 0 },
      colorStops: list
        .slice()
        .sort((a, b) => a.offset - b.offset)
        .map((s) => ({ offset: s.offset / 100, color: s.color })),
    });
  }

  /** Cache of painted brush-texture tiles keyed by `${brushStyle}|${color}`, so re-picking the same color/style (or every re-render while typing) doesn't repaint the canvas from scratch. Tiles are read-only <canvas> sources, so they're safe to share — see buildBrushFill. */
  private brushTileCache = new Map<string, HTMLCanvasElement>();

  /**
   * The "Brush" fill option — a painterly texture in the chosen color,
   * varying by `brushStyle` (see BRUSH_STYLES / paintBrushTile) — instead of
   * a flat, perfectly smooth fill. Returns a fresh Pattern instance every
   * call (never share one Pattern across multiple objects — Fabric mutates
   * offsetX/patternTransform per-object) wrapping a cached, reused <canvas>
   * tile for that style+color combo. Used for shape fill, shape border
   * (stroke), and text color.
   */
  private buildBrushFill(color: string, brushStyle?: string): Pattern {
    const style = brushStyle || DEFAULT_BRUSH_STYLE;
    const key = `${style}|${color}`;
    let tile = this.brushTileCache.get(key);
    if (!tile) {
      tile = this.paintBrushTile(color, style);
      this.brushTileCache.set(key, tile);
    }
    return new Pattern({ source: tile, repeat: 'repeat' });
  }

  /**
   * Paints one 160x160 texture tile for a given brush style + base color.
   * Every style starts with a fully-opaque base coat of `color` (so the
   * fill never shows transparent gaps to the artboard behind it) and then
   * layers lighter/darker marks of the same color on top — streaks, dots,
   * or blobs depending on the style — using a tiny deterministic PRNG (not
   * Math.random()) so the same style+color always paints the same texture;
   * text rebuilds its Fabric object on every keystroke, and a truly random
   * texture would visibly "shimmer" on every render instead of looking like
   * one settled brush stroke.
   */
  private paintBrushTile(color: string, brushStyle: string): HTMLCanvasElement {
    const size = 160;
    const tile = document.createElement('canvas');
    tile.width = size;
    tile.height = size;
    const ctx = tile.getContext('2d')!;

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, size, size);

    const { r, g, b } = ImageEditorCanvasService.hexToRgb(color);
    let seed = 17;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const shade = (amt: number) => {
      const lighter = rand() > 0.45;
      const channel = (c: number) => Math.max(0, Math.min(255, lighter ? c + amt : c - amt));
      return { r: channel(r), g: channel(g), b: channel(b) };
    };
    const streak = (
      count: number,
      widthRange: [number, number],
      alphaRange: [number, number],
      shadeAmtRange: [number, number],
      reachFactor = 1.1,
      bendFactor = 0.5,
    ) => {
      ctx.lineCap = 'round';
      for (let i = 0; i < count; i++) {
        const c = shade(shadeAmtRange[0] + rand() * (shadeAmtRange[1] - shadeAmtRange[0]));
        const a = alphaRange[0] + rand() * (alphaRange[1] - alphaRange[0]);
        ctx.strokeStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${a.toFixed(2)})`;
        ctx.lineWidth = widthRange[0] + rand() * (widthRange[1] - widthRange[0]);

        const x1 = rand() * size;
        const y1 = rand() * size;
        const x2 = x1 + (rand() - 0.5) * size * reachFactor;
        const y2 = y1 + (rand() - 0.5) * size * reachFactor;
        const cx = (x1 + x2) / 2 + (rand() - 0.5) * size * bendFactor;
        const cy = (y1 + y2) / 2 + (rand() - 0.5) * size * bendFactor;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(cx, cy, x2, y2);
        ctx.stroke();
      }
    };
    const dots = (count: number, radiusRange: [number, number], alphaRange: [number, number], shadeAmtRange: [number, number]) => {
      for (let i = 0; i < count; i++) {
        const c = shade(shadeAmtRange[0] + rand() * (shadeAmtRange[1] - shadeAmtRange[0]));
        const a = alphaRange[0] + rand() * (alphaRange[1] - alphaRange[0]);
        ctx.fillStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${a.toFixed(2)})`;
        const radius = radiusRange[0] + rand() * (radiusRange[1] - radiusRange[0]);
        ctx.beginPath();
        ctx.arc(rand() * size, rand() * size, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    switch (brushStyle) {
      case 'wetBristle':
        // Fewer, wider, softer, more overlapping strokes — a blended look.
        streak(20, [10, 30], [0.08, 0.18], [25, 55], 1.2, 0.6);
        break;
      case 'pointillist':
        // Scattered dots instead of streaks.
        dots(140, [1.5, 5], [0.18, 0.5], [30, 70]);
        break;
      case 'smoothStroke':
        // A handful of broad, nearly-straight, very soft strokes.
        streak(7, [26, 55], [0.05, 0.12], [15, 35], 0.9, 0.15);
        break;
      case 'grainyGouache':
        // Lots of tiny short streaks — fine, sandy grain.
        streak(90, [1, 3], [0.12, 0.3], [30, 70], 0.15, 0.1);
        break;
      case 'onPaper':
        // Large soft mottled blotches (paper mottling) plus a fine dot grain on top.
        dots(24, [14, 38], [0.04, 0.1], [20, 45]);
        dots(90, [0.5, 1.6], [0.08, 0.18], [40, 80]);
        break;
      case 'dryBristle':
      default:
        // Sparse, thin, higher-contrast jagged streaks — original default look.
        streak(28, [5, 27], [0.1, 0.32], [35, 80], 1.1, 0.5);
        break;
    }

    return tile;
  }

  private static hexToRgb(hex: string): { r: number; g: number; b: number } {
    const clean = (hex || '#808080').replace('#', '').trim();
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const num = parseInt(full, 16);
    if (Number.isNaN(num) || full.length !== 6) return { r: 128, g: 128, b: 128 };
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }

  /**
   * Combines a swatch color with a 0-100 transparency % into a renderable
   * CSS color — backs every "transparency slider" popup (fill, text
   * background, shadow, text outline). Always re-derives from the raw r/g/b
   * channels rather than passing an already-rgba string through untouched,
   * so this keeps working even for a color that was itself saved as an
   * rgba() (the pre-slider shadow default did this — see buildShadow) —
   * whatever alpha was baked into THAT string is discarded in favor of the
   * opacity % actually being asked for here.
   */
  private colorWithOpacity(color: string | undefined, opacityPercent: number | undefined, fallbackHex: string): string {
    const pct = Math.max(0, Math.min(100, opacityPercent ?? 100));
    const rgbaMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color || '');
    const { r, g, b } = rgbaMatch
      ? { r: +rgbaMatch[1], g: +rgbaMatch[2], b: +rgbaMatch[3] }
      : ImageEditorCanvasService.hexToRgb(color || fallbackHex);
    return pct >= 100 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${(pct / 100).toFixed(2)})`;
  }

  /**
   * Builds a `fabric.Shadow` from the generic shadow props any layer type can
   * carry, or undefined if fully transparent (opacity 0). `shadowOpacity` is
   * the current slider value (0-100); scenes saved before the transparency
   * slider existed only have the old on/off `shadowEnabled` boolean, so that
   * still gates a sensible fallback — inferring the opacity that was already
   * baked into the pre-slider default color (`rgba(0,0,0,0.55)`) so an old,
   * untouched shadow keeps rendering exactly as before.
   */
  private buildShadow(props: Record<string, any>): Shadow | undefined {
    const rawColor = props['shadowColor'] || '#000000';
    const legacyAlpha = /^rgba\([^)]+,\s*([\d.]+)\s*\)$/i.exec(rawColor);
    const legacyOpacity = legacyAlpha ? Math.round(parseFloat(legacyAlpha[1]) * 100) : 100;
    const fallbackOpacity = props['shadowEnabled'] ? legacyOpacity : 0;
    const opacity = props['shadowOpacity'] ?? fallbackOpacity;
    if (opacity <= 0) return undefined;

    return new Shadow({
      color: this.colorWithOpacity(rawColor, opacity, '#000000'),
      blur: props['shadowBlur'] ?? 14,
      offsetX: props['shadowOffsetX'] ?? 4,
      offsetY: props['shadowOffsetY'] ?? 4,
    });
  }

  /**
   * Builds the text layer's Fabric object from its full prop bag. Straight
   * text (curve === 0) is a real `IText` — natively click-to-edit. Curved
   * text (curve !== 0) is baked into a `Group` of individually-positioned
   * characters arranged along a circular arc — Fabric has no native curved
   * text, so this positions each character (measured with a throwaway
   * FabricText for accurate per-glyph width) at its own point/rotation on
   * the arc. Editing curved text content happens via the attributes panel's
   * text field, which triggers a full rebuild rather than in-place editing.
   */
  private buildTextObject(props: Record<string, any>): FabricObject {
    const curve = props['curve'] || 0;
    const fontFamily = props['fontFamily'] || 'Noto Sans';
    const fontSize = props['fontSize'] || 42;
    const textColor = props['color'] || '#1f2937';
    const fill =
      props['fillMode'] === 'brush' ? this.buildBrushFill(textColor, props['brushStyle']) : textColor;
    const fontWeight = props['bold'] ? 'bold' : 'normal';
    const fontStyle = props['italic'] ? 'italic' : 'normal';
    const underline = !!props['underline'];
    const linethrough = !!props['linethrough'];
    const charSpacing = props['letterSpacing'] || 0; // fabric units: 1/1000 em
    const lineHeight = props['lineHeight'] || 1.16;
    const shadow = this.buildShadow(props);

    // Text outline/border — stroke is drawn first so the fill sits cleanly
    // on top of it (paintFirst: 'stroke'), giving a crisp outline rather
    // than the stroke half-covering the fill's edges (Fabric's default
    // paint order draws fill first, which looks muddier for an outline effect).
    // textStrokeOpacity (0-100, via the transparency slider) is what actually
    // gates visibility now; textStrokeEnabled is only read as a fallback for
    // scenes saved before the slider existed.
    const textStrokeOpacity = props['textStrokeOpacity'] ?? (props['textStrokeEnabled'] ? 100 : 0);
    const textStrokeWidth = textStrokeOpacity > 0 ? props['textStrokeWidth'] ?? 2 : 0;
    const textStroke = textStrokeWidth
      ? this.colorWithOpacity(props['textStrokeColor'] || '#000000', textStrokeOpacity, '#000000')
      : undefined;
    const paintFirst = textStrokeWidth ? ('stroke' as const) : undefined;

    // Highlight-behind-text background — same opacity-slider-gates-visibility
    // pattern as the outline above. backgroundColor's mere presence used to
    // be the on/off signal; a scene saved before the slider existed still
    // has a truthy backgroundColor with no backgroundOpacity, so that's the
    // fallback for "was this on".
    const backgroundOpacity = props['backgroundOpacity'] ?? (props['backgroundColor'] ? 100 : 0);
    const textBackgroundColor =
      backgroundOpacity > 0
        ? this.colorWithOpacity(props['backgroundColor'] || '#ffe27a', backgroundOpacity, '#ffe27a')
        : undefined;

    if (curve === 0) {
      return new IText(props['text'] || 'Double-click to edit', {
        fontFamily,
        fontSize,
        fill,
        textAlign: props['align'] || 'center',
        fontWeight,
        fontStyle,
        underline,
        linethrough,
        charSpacing,
        lineHeight,
        originX: 'center',
        originY: 'center',
        backgroundColor: textBackgroundColor,
        shadow,
        stroke: textStroke,
        strokeWidth: textStrokeWidth,
        paintFirst,
      });
    }

    // Curved text: lay characters out along an arc. `dir` flips the arc
    // upward (curve > 0, "cap" ⌢) vs downward (curve < 0, "cup" ⌣) — this is
    // a true vertical mirror (y -> -y, angle -> -angle), so characters stay
    // correctly oriented (never upside down) in either direction.
    const text: string = ((props['text'] ?? '') as string).replace(/\n/g, ' ') || ' ';
    const chars: string[] = Array.from(text);
    const minRadius = 150;
    const maxRadius = 1400;
    const radius =
      maxRadius - (Math.min(100, Math.abs(curve)) / 100) * (maxRadius - minRadius);
    const dir = curve >= 0 ? 1 : -1;
    const extraSpacing = (charSpacing / 1000) * fontSize;

    const widths = chars.map(
      (ch) =>
        (new FabricText(ch === ' ' ? ' ' : ch, { fontFamily, fontSize, fontWeight, fontStyle })
          .width || fontSize * 0.5) + extraSpacing,
    );
    const totalWidth = widths.reduce((a, b) => a + b, 0);
    const totalAngle = totalWidth / radius;

    let cursor = -totalAngle / 2;
    const charObjects: FabricObject[] = chars.map((ch, i) => {
      const w = widths[i];
      const theta = cursor + w / 2 / radius;
      cursor += w / radius;

      const x = radius * Math.sin(theta);
      const y = -dir * radius * (1 - Math.cos(theta));
      const rotationDeg = dir * theta * (180 / Math.PI);

      return new FabricText(ch, {
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        underline,
        linethrough,
        fill,
        left: x,
        top: y,
        originX: 'center',
        originY: 'center',
        angle: rotationDeg,
        shadow,
        stroke: textStroke,
        strokeWidth: textStrokeWidth,
        paintFirst,
      });
    });

    return new Group(charObjects, { originX: 'center', originY: 'center' });
  }

  /** Builds a decoration/shape layer's Fabric object — rect, circle, triangle, diamond, pentagon, hexagon, star, heart, arrow, or speech-bubble — with fill (solid/transparent/gradient), border (none/solid/dashed/dotted, solid or gradient color), and shadow. */
  private buildShapeObject(props: Record<string, any>): FabricObject {
    let shapeType: string = props['shapeType'] || 'rect';
    if (shapeType === 'ellipse') shapeType = 'circle'; // legacy alias from before the shape set was expanded

    // Fill: solid (with a 0-100 transparency slider), a multi-stop gradient,
    // or a painterly "Brush" texture in the chosen color. fillOpacity is what
    // actually gates visibility for 'solid' now; the old on/off
    // fillTransparent boolean is only read as a fallback for scenes saved
    // before the slider existed.
    const fillOpacity = props['fillOpacity'] ?? (props['fillTransparent'] ? 0 : 100);
    const fill =
      props['fillMode'] === 'gradient'
        ? this.buildGradient(props['fillGradientStops'])
        : props['fillMode'] === 'brush'
          ? this.buildBrushFill(props['fill'] || '#2781ff', props['brushStyle'])
          : fillOpacity <= 0
            ? 'transparent'
            : this.colorWithOpacity(props['fill'], fillOpacity, '#2781ff');

    // Border: 'none' (invisible — the default for new shapes) / 'solid' /
    // 'dashed' / 'dotted', each with a solid or gradient color. Falls back to
    // inferring 'solid' from a legacy strokeWidth-only scene for backward
    // compatibility with scenes saved before strokeStyle existed.
    const strokeStyle: string = props['strokeStyle'] || (props['strokeWidth'] ? 'solid' : 'none');
    let stroke: any = undefined;
    let strokeWidth = 0;
    let strokeDashArray: number[] | undefined;
    let strokeLineCap: 'butt' | 'round' | 'square' | undefined;
    if (strokeStyle !== 'none') {
      strokeWidth = props['strokeWidth'] ?? 4;
      stroke =
        props['strokeMode'] === 'gradient'
          ? this.buildGradient(props['strokeGradientStops'])
          : props['strokeMode'] === 'brush'
            ? this.buildBrushFill(props['stroke'] || '#000000', props['strokeBrushStyle'])
            : props['stroke'] || '#000000';
      if (strokeStyle === 'dashed') {
        strokeDashArray = [strokeWidth * 2.5, strokeWidth * 1.6];
      } else if (strokeStyle === 'dotted') {
        strokeDashArray = [0.001, strokeWidth * 1.8];
        strokeLineCap = 'round';
      }
    }

    const shadow = this.buildShadow(props);
    const common = {
      fill,
      stroke,
      strokeWidth,
      strokeDashArray,
      strokeLineCap,
      shadow,
      originX: 'center' as const,
      originY: 'center' as const,
    };

    const shape: FabricObject = (() => {
      switch (shapeType) {
        case 'circle':
          return new Circle({ radius: 75, ...common });
        case 'triangle':
          return new Triangle({ width: 160, height: 150, ...common });
        case 'diamond':
          return new Polygon(regularPolygonPoints(4, 90), { ...common });
        case 'pentagon':
          return new Polygon(regularPolygonPoints(5, 85), { ...common });
        case 'hexagon':
          return new Polygon(regularPolygonPoints(6, 85), { ...common });
        case 'star': {
          return new Polygon(starPoints(5, 85, 33), { ...common });
        }
        case 'arrow': {
          const arrow = new Polygon(arrowPoints(), { ...common });
          arrow.scaleToWidth(170);
          return arrow;
        }
        case 'heart': {
          const heart = new Path(HEART_PATH, { ...common });
          heart.scaleToWidth(150);
          return heart;
        }
        case 'speech-bubble': {
          const bubble = new Path(SPEECH_BUBBLE_PATH, { ...common });
          bubble.scaleToWidth(160);
          return bubble;
        }
        case 'rect':
        default:
          return new Rect({
            width: 160,
            height: 160,
            rx: props['rx'] || 0,
            ry: props['rx'] || 0,
            ...common,
          });
      }
    })();

    const blurType = props['blurType'];
    if ((blurType === 'linear' || blurType === 'radial') && props['blurAmount'] > 0) {
      this.applyBlur(shape, blurType, props['blurAmount']);
    }

    return shape;
  }

  /**
   * Applies a lightweight per-object blur by monkey-patching this specific
   * object instance's own _render — safe in this codebase specifically
   * because every attribute change already rebuilds a brand-new object from
   * scratch (see rebuildObject), so there's never a stale patched instance
   * left over that needs un-patching when blur is turned off; the next
   * rebuild just doesn't call this again.
   *
   * Fabric's built-in filters (filters.Blur etc.) only apply to raster
   * FabricImage objects, not vector shapes, so there's no built-in way to
   * blur a Rect/Circle/Polygon/Path directly. Two techniques instead:
   * - 'radial': the canvas 2D context's native `filter: blur()` — a real,
   *   GPU-accelerated Gaussian blur, uniform in every direction.
   * - 'linear': Fabric's filter pipeline has no directional/motion-blur
   *   filter to fall back on, so this composites several faint copies of
   *   the shape translated along one axis — a standard, safe way to fake a
   *   directional blur without a custom convolution shader.
   */
  private applyBlur(obj: FabricObject, blurType: 'linear' | 'radial', blurAmount: number): void {
    const amount = Math.max(0, Math.min(30, blurAmount));
    if (amount <= 0) return;
    const originalRender = (obj as any)._render.bind(obj) as (ctx: CanvasRenderingContext2D) => void;

    if (blurType === 'radial') {
      (obj as any)._render = (ctx: CanvasRenderingContext2D) => {
        ctx.save();
        ctx.filter = `blur(${amount}px)`;
        originalRender(ctx);
        ctx.restore();
      };
    } else {
      (obj as any)._render = (ctx: CanvasRenderingContext2D) => {
        const steps = 8;
        ctx.save();
        const baseAlpha = ctx.globalAlpha ?? 1;
        ctx.globalAlpha = baseAlpha / steps;
        for (let i = 0; i < steps; i++) {
          const t = (i / (steps - 1) - 0.5) * amount;
          ctx.save();
          ctx.translate(t, 0);
          originalRender(ctx);
          ctx.restore();
        }
        ctx.restore();
      };
    }
    obj.dirty = true;
  }

  async addLayer(layer: EditorLayer): Promise<void> {
    if (!this.canvas) return;
    const canvas = this.canvas;
    const props = { ...layer.props };
    let obj: FabricObject | undefined;

    switch (layer.type) {
      case 'background-image':
      case 'photo':
      case 'logo': {
        const img = await this.loadImageSafely(props['sourceUrl']);
        if (layer.type === 'background-image') {
          const { width, height } = this.artboardSize;
          const scale = Math.max(width / (img.width || 1), height / (img.height || 1));
          img.set({
            left: width / 2,
            top: height / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
          });
        } else {
          const targetW = layer.type === 'logo' ? 140 : this.artboardSize.width * 0.5;
          const scale = targetW / (img.width || targetW);
          img.set({
            left: layer.x || this.artboardSize.width / 2,
            top: layer.y || this.artboardSize.height / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
          });
        }
        img.set({ shadow: this.buildShadow(props) });
        obj = img;
        break;
      }
      case 'text': {
        obj = this.buildTextObject(props);
        obj.set({
          left: layer.x || this.artboardSize.width / 2,
          top: layer.y || this.artboardSize.height / 2,
        });
        break;
      }
      case 'decoration': {
        obj = this.buildShapeObject(props);
        obj.set({
          left: layer.x || this.artboardSize.width / 2,
          top: layer.y || this.artboardSize.height / 2,
        });
        break;
      }
      case 'qr': {
        const targetUrl = props['targetUrl'] || '';
        const dataUrl = await QRCode.toDataURL(targetUrl || ' ', {
          margin: 1,
          width: 400,
          color: { dark: '#000000', light: '#ffffff00' },
        });
        const img = await FabricImage.fromURL(dataUrl);
        img.set({
          left: layer.x || this.artboardSize.width / 2,
          top: layer.y || this.artboardSize.height / 2,
          originX: 'center',
          originY: 'center',
          scaleX: 140 / (img.width || 140),
          scaleY: 140 / (img.height || 140),
        });
        obj = img;
        break;
      }
    }

    if (!obj) return;

    (obj as TaggedFabricObject).data = { layerId: layer.id, layerType: layer.type, props };
    if (layer.rotation) obj.set({ angle: layer.rotation });

    canvas.add(obj);

    if (layer.type === 'background-image') {
      canvas.sendObjectToBack(obj);
    } else {
      canvas.setActiveObject(obj);
    }
    canvas.requestRenderAll();
    this.changed$.next();
  }

  removeLayer(layerId: string): void {
    const obj = this.findObject(layerId);
    if (!obj || !this.canvas) return;
    this.canvas.remove(obj);
    this.canvas.requestRenderAll();
    this.changed$.next();
  }

  /**
   * Applies a prop patch to a layer. Position/rotation/opacity are applied
   * as incremental Fabric transforms for every layer type. Everything else
   * (text content/font/curve/shadow/background, shape fill/stroke/gradient/
   * shadow) triggers a full rebuild of the underlying Fabric object for
   * 'text' and 'decoration' layers — simpler and more robust than trying to
   * incrementally patch a dozen interdependent visual properties, and it's
   * the only way to swap between a plain IText and a curved character Group.
   * Image-like layers (photo/logo/background/qr) apply shadow incrementally
   * since they never need a structural rebuild.
   */
  updateLayer(layerId: string, patch: Record<string, any>): void {
    const obj = this.findObject(layerId);
    if (!obj || !this.canvas || !obj.data) return;

    const { x, y, rotation, opacity, ...contentPatch } = patch;
    const mergedProps = { ...obj.data.props, ...contentPatch };
    const type = obj.data.layerType;

    let target: TaggedFabricObject = obj;

    // customName is purely cosmetic (the Layers panel label) — it never
    // affects the rendered object, so it shouldn't force a rebuild the way
    // every other text/decoration prop change does.
    const rebuildTriggeringKeys = Object.keys(contentPatch).filter((k) => k !== 'customName');

    if ((type === 'text' || type === 'decoration') && rebuildTriggeringKeys.length > 0) {
      target = this.rebuildObject(obj, type, mergedProps);
    } else {
      obj.data.props = mergedProps;
      const shadowKeys = ['shadowEnabled', 'shadowColor', 'shadowBlur', 'shadowOffsetX', 'shadowOffsetY'];
      if (shadowKeys.some((k) => k in contentPatch)) {
        obj.set({ shadow: this.buildShadow(mergedProps) });
      }
    }

    const transformPatch: Record<string, any> = {};
    if (x !== undefined) transformPatch['left'] = x;
    if (y !== undefined) transformPatch['top'] = y;
    if (rotation !== undefined) transformPatch['angle'] = rotation;
    if (opacity !== undefined) transformPatch['opacity'] = opacity;
    if (Object.keys(transformPatch).length) {
      target.set(transformPatch);
    }
    target.setCoords();

    this.canvas.requestRenderAll();
    this.changed$.next();

    const active = this.canvas.getActiveObject() as TaggedFabricObject | undefined;
    if (active === target) {
      this.selection$.next(this.toLayer(target));
    }
  }

  /** Swaps out the Fabric object backing a text/decoration layer, preserving its id, transform, and z-order. */
  private rebuildObject(
    old: TaggedFabricObject,
    type: 'text' | 'decoration',
    mergedProps: Record<string, any>,
  ): TaggedFabricObject {
    const canvas = this.canvas!;
    const index = canvas.getObjects().indexOf(old);
    const transform = {
      left: old.left,
      top: old.top,
      angle: old.angle,
      scaleX: old.scaleX,
      scaleY: old.scaleY,
      opacity: old.opacity,
    };
    const layerId = old.data!.layerId;
    canvas.remove(old);

    const fresh = (
      type === 'text' ? this.buildTextObject(mergedProps) : this.buildShapeObject(mergedProps)
    ) as TaggedFabricObject;
    fresh.set(transform);
    fresh.data = { layerId, layerType: type, props: mergedProps };
    canvas.insertAt(index, fresh);
    canvas.setActiveObject(fresh);
    fresh.setCoords();
    return fresh;
  }

  bringForward(layerId: string): void {
    const obj = this.findObject(layerId);
    if (!obj || !this.canvas) return;
    this.canvas.bringObjectForward(obj);
    this.canvas.requestRenderAll();
    this.changed$.next();
  }

  sendBackward(layerId: string): void {
    const obj = this.findObject(layerId);
    if (!obj || !this.canvas) return;
    this.canvas.sendObjectBackwards(obj);
    this.canvas.requestRenderAll();
    this.changed$.next();
  }

  deselect(): void {
    this.canvas?.discardActiveObject();
    this.canvas?.requestRenderAll();
  }

  // ─────────────────────────────────────────────
  // KEYBOARD SHORTCUTS — copy / paste / group / ungroup / delete
  // ─────────────────────────────────────────────

  /** Deletes whatever is currently selected — a single object or a multi-selection (Fabric's ActiveSelection), keyed off Fabric's own getActiveObjects() rather than the single activeLayer signal so multi-select Delete works too. */
  deleteSelection(): void {
    if (!this.canvas) return;
    const objects = this.canvas.getActiveObjects();
    if (!objects.length) return;
    this.canvas.discardActiveObject();
    objects.forEach((obj) => this.canvas!.remove(obj));
    this.canvas.requestRenderAll();
    this.changed$.next();
    this.selection$.next(null);
  }

  async copySelection(): Promise<void> {
    const active = this.canvas?.getActiveObject() as TaggedFabricObject | undefined;
    if (!active) return;
    const cloned = (await active.clone()) as TaggedFabricObject;
    // Fabric's clone() round-trips through toObject()/fromObject() and does
    // NOT carry over our custom `data` tag (layerId/layerType/props) — left
    // unset, the next paste's toLayer() falls back to a bogus 'decoration'
    // (shape) layer regardless of what was actually copied (e.g. text).
    // Stamp it back on explicitly here so pasteClipboard() has a real source.
    cloned.data = active.data
      ? { layerId: active.data.layerId, layerType: active.data.layerType, props: { ...active.data.props } }
      : undefined;
    this.clipboard = cloned;
    this.pasteOffset = 0;
  }

  /** Pastes a fresh clone of the clipboard, offset a bit further each repeated paste so copies don't stack exactly on top of each other, tagged with a brand-new layerId (never reuse the copied object's id). */
  async pasteClipboard(): Promise<void> {
    if (!this.canvas || !this.clipboard) return;
    this.pasteOffset += 24;
    const cloned = (await this.clipboard.clone()) as TaggedFabricObject;
    const srcData = (this.clipboard as TaggedFabricObject).data;
    cloned.set({
      left: (this.clipboard.left ?? 0) + this.pasteOffset,
      top: (this.clipboard.top ?? 0) + this.pasteOffset,
    });
    cloned.data = srcData
      ? { layerId: crypto.randomUUID(), layerType: srcData.layerType, props: { ...srcData.props } }
      : undefined;
    this.canvas.add(cloned);
    this.canvas.setActiveObject(cloned);
    cloned.setCoords();
    this.canvas.requestRenderAll();
    this.changed$.next();
  }

  /**
   * Groups the current multi-selection into a single persistent Group layer.
   * Fabric 5's ActiveSelection#toGroup() convenience method was REMOVED in
   * Fabric 6's group rewrite (confirmed: no toGroup/toActiveSelection
   * anywhere in the installed fabric@6 source) — calling it silently did
   * nothing, which is why this used to be a dead button. The v6-correct
   * manual replacement (per fabricjs/fabric.js#9854): read the selection's
   * objects, discard the selection (this restores each object's canvas-
   * absolute left/top — ActiveSelection#onDeselect() exits every child), pull
   * them off the canvas, and hand them to a fresh Group's constructor, which
   * computes the group's own bounding box from their current positions.
   */
  groupSelection(): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const active = canvas.getActiveObject() as any;
    if (!active || active.type !== 'activeselection') return;

    const objects = (active.getObjects() as FabricObject[]).slice();
    if (objects.length < 2) return;

    canvas.discardActiveObject();
    objects.forEach((obj) => canvas.remove(obj));

    const group = new Group(objects, { originX: 'center', originY: 'center' }) as TaggedFabricObject;
    group.data = { layerId: crypto.randomUUID(), layerType: 'decoration', props: { shapeType: 'group' } };
    canvas.add(group);
    canvas.setActiveObject(group);
    group.setCoords();
    canvas.requestRenderAll();
    this.changed$.next();
  }

  /**
   * Reverses groupSelection(). Same v6 API removal as above (no
   * Group#toActiveSelection() anymore) — the manual replacement is
   * Group#removeAll(), a real, still-present method that ejects every child
   * AND (unlike ActiveSelection's override of the same exit path) applies
   * the group's own transform back onto each one, so they land in the same
   * visual spot on the canvas as they appeared inside the group.
   */
  ungroupSelection(): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const active = canvas.getActiveObject() as any;
    if (!active || active.type !== 'group') return;

    const group = active as Group;
    const objects = group.removeAll();
    canvas.remove(group);
    objects.forEach((obj) => {
      canvas.add(obj);
      obj.setCoords();
    });
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    this.changed$.next();
    this.selectionCount$.next(0);
    this.selection$.next(null);
  }

  /**
   * Aligns every object in the current multi-selection to an edge/center of
   * the selection's own bounding box. Every layer object is built with
   * originX/originY 'center', and a Fabric ActiveSelection positions its
   * children's left/top relative to the selection's own center — so "align
   * left" for a child is simply "half the selection width to the left of
   * center, plus half the child's own width", etc.
   */
  alignSelection(
    mode: 'left' | 'center-h' | 'right' | 'top' | 'middle-v' | 'bottom',
  ): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const active = canvas.getActiveObject() as any;
    if (!active || active.type !== 'activeselection') return;

    const objects = active.getObjects() as FabricObject[];
    const selWidth = active.width * (active.scaleX ?? 1);
    const selHeight = active.height * (active.scaleY ?? 1);

    objects.forEach((obj) => {
      const w = obj.getScaledWidth();
      const h = obj.getScaledHeight();
      switch (mode) {
        case 'left':
          obj.set({ left: -selWidth / 2 + w / 2 });
          break;
        case 'center-h':
          obj.set({ left: 0 });
          break;
        case 'right':
          obj.set({ left: selWidth / 2 - w / 2 });
          break;
        case 'top':
          obj.set({ top: -selHeight / 2 + h / 2 });
          break;
        case 'middle-v':
          obj.set({ top: 0 });
          break;
        case 'bottom':
          obj.set({ top: selHeight / 2 - h / 2 });
          break;
      }
      obj.setCoords();
    });

    active.setCoords();
    canvas.requestRenderAll();
    this.changed$.next();
  }

  /**
   * Moves whatever's currently selected by (dx, dy) canvas px — used for
   * arrow-key nudging. `getActiveObject()` returns either a single tagged
   * object or a multi-select ActiveSelection; both expose left/top the same
   * way, so this works unmodified for either case (nudging a multi-selection
   * moves every object in it together, since Fabric moves the ActiveSelection
   * as one and each child's position is relative to it).
   */
  nudgeSelection(dx: number, dy: number): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const active = canvas.getActiveObject() as TaggedFabricObject | undefined;
    if (!active) return;

    active.set({
      left: (active.left ?? 0) + dx,
      top: (active.top ?? 0) + dy,
    });
    active.setCoords();
    canvas.requestRenderAll();
    this.changed$.next();

    // Single-object selection: refresh selection$ so the attributes panel's
    // Position X/Y fields track the nudge live. A multi-select
    // ActiveSelection has no .data of its own (see emitSelection in
    // initCanvas), so there's nothing to refresh there.
    if (active.data) {
      this.selection$.next(this.toLayer(active));
    }
  }

  /** Selects a layer by id — used by the Layers panel so clicking a row selects that object on the canvas. */
  selectLayer(layerId: string): void {
    const obj = this.findObject(layerId);
    if (!obj || !this.canvas) return;
    this.canvas.setActiveObject(obj);
    this.canvas.requestRenderAll();
    this.selection$.next(this.toLayer(obj));
  }

  /** Swaps the pixel source of an existing image/photo/logo/background layer in place (keeps position, scale, id). */
  async replaceImageLayerSource(layerId: string, sourceUrl: string): Promise<void> {
    const tagged = this.findObject(layerId);
    if (!tagged || !this.canvas || !(tagged instanceof FabricImage)) return;
    const obj = tagged as FabricImage & TaggedFabricObject;

    await this.setImageSrcSafely(obj, sourceUrl);
    if (obj.data) obj.data.props = { ...obj.data.props, sourceUrl };

    if (obj.data?.layerType === 'background-image') {
      const { width, height } = this.artboardSize;
      const scale = Math.max(width / (obj.width || 1), height / (obj.height || 1));
      obj.set({ scaleX: scale, scaleY: scale, left: width / 2, top: height / 2 });
      obj.setCoords();
    }

    this.canvas.requestRenderAll();
    this.changed$.next();

    const active = this.canvas.getActiveObject() as TaggedFabricObject | undefined;
    if (active === obj) this.selection$.next(this.toLayer(obj));
  }

  /** Re-renders a QR layer's image for a new target URL, keeping the same layer id/position. */
  async regenerateQr(layerId: string, targetUrl: string): Promise<void> {
    const tagged = this.findObject(layerId);
    if (!tagged || !this.canvas || !(tagged instanceof FabricImage)) return;
    const obj = tagged as FabricImage & TaggedFabricObject;

    const dataUrl = await QRCode.toDataURL(targetUrl || ' ', {
      margin: 1,
      width: 400,
      color: { dark: '#000000', light: '#ffffff00' },
    });
    await obj.setSrc(dataUrl);
    if (obj.data) obj.data.props = { ...obj.data.props, targetUrl };
    this.canvas.requestRenderAll();
    this.changed$.next();

    const active = this.canvas.getActiveObject() as TaggedFabricObject | undefined;
    if (active === obj) this.selection$.next(this.toLayer(obj));
  }

  private findObject(layerId: string): TaggedFabricObject | undefined {
    return this.canvas
      ?.getObjects()
      .find((o) => (o as TaggedFabricObject).data?.layerId === layerId) as
      | TaggedFabricObject
      | undefined;
  }

  private toLayer(obj: TaggedFabricObject): EditorLayer {
    const data = obj.data || {
      layerId: crypto.randomUUID(),
      layerType: 'decoration' as const,
      props: {},
    };
    // A grouped selection (groupSelection()) is a real Fabric Group holding
    // the original objects as children, each still carrying its own .data —
    // surface them here purely for the Layers panel's indented child rows.
    // Their x/y are group-LOCAL coordinates (relative to the group's own
    // center), not artboard coordinates, so they're display-only.
    const children =
      obj instanceof Group
        ? obj.getObjects().map((child) => this.toLayer(child as TaggedFabricObject))
        : undefined;
    return {
      id: data.layerId,
      type: data.layerType,
      x: obj.left ?? 0,
      y: obj.top ?? 0,
      width: obj.getScaledWidth(),
      height: obj.getScaledHeight(),
      rotation: obj.angle ?? 0,
      zIndex: this.canvas?.getObjects().indexOf(obj) ?? 0,
      props: { ...data.props },
      ...(children && children.length ? { children } : {}),
    };
  }

  getScene(): EditorScene {
    if (!this.canvas) {
      return { version: 1, aspectRatio: this.aspectRatio, layers: [] };
    }
    const layers = this.canvas
      .getObjects()
      .map((o) => this.toLayer(o as TaggedFabricObject));
    return { version: 1, aspectRatio: this.aspectRatio, layers };
  }

  /** Wipes the canvas and rebuilds it from `scene` — used by the host component's Cancel button to revert to the last-saved state. Ends with nothing selected, regardless of what addLayer() leaves active mid-load. */
  async loadScene(scene: EditorScene): Promise<void> {
    if (!this.canvas) return;
    this.canvas.discardActiveObject();
    this.canvas.clear();
    this.canvas.backgroundColor = '#ffffff';
    for (const layer of scene.layers.slice().sort((a, b) => a.zIndex - b.zIndex)) {
      await this.addLayer(layer);
    }
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
    this.selection$.next(null);
    this.selectionCount$.next(0);
    this.changed$.next();
  }

  getActiveLayer(): EditorLayer | null {
    const active = this.canvas?.getActiveObject() as TaggedFabricObject | undefined;
    return active ? this.toLayer(active) : null;
  }

  /**
   * Flattens the canvas to a Blob for upload.
   * - Waits on document.fonts.ready to dodge the font-load race.
   * - Every image layer is routed through loadImageSafely()'s tiered
   *   fallback, which almost always avoids a CORS-tainted canvas — but if
   *   every tier had to fall back to a plain, non-CORS image load
   *   (taintedExport), the browser will refuse to read the canvas at all.
   *   That previously surfaced as a bare SecurityError caught by the host
   *   component's generic catch block and only logged to the console — from
   *   the user's point of view, clicking Save did nothing. Now it throws a
   *   specific, actionable message instead.
   */
  async exportToBlob(format: 'png' | 'jpeg' = 'png'): Promise<Blob> {
    if (!this.canvas) throw new Error('Canvas not initialized');
    if ('fonts' in document) {
      await (document as any).fonts.ready;
    }
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();

    const taintedMessage =
      "One of the photos on this artboard couldn't be loaded securely (a cross-origin restriction from wherever it was picked), so the image can't be exported. Try replacing that photo via \"From device\" instead.";

    let blob: Blob | null;
    try {
      blob = await this.canvas.toBlob({ format, multiplier: 2, quality: 0.92 });
    } catch (err) {
      throw new Error(this.taintedExport ? taintedMessage : (err as Error)?.message || 'Canvas export failed');
    }
    if (!blob) {
      throw new Error(this.taintedExport ? taintedMessage : 'Canvas export produced an empty result');
    }
    return blob;
  }

  /** Also useful for cheap thumbnails/previews without a full upload round-trip. */
  toPreviewDataUrl(): string {
    if (!this.canvas) return '';
    return this.canvas.toDataURL({ format: 'png', multiplier: 0.35 });
  }

  destroy(): void {
    this.canvas?.dispose();
    this.canvas = undefined;
    this.selection$.complete();
    this.changed$.complete();
  }
}
